import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { RegsApiService } from '../../data/regs-api.service';
import { ChangeEvent, Followable, Sector, Species } from '../../domain/regs-models';

@Component({
  selector: 'app-keep-it-changes-page',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="changes-page">
      <div class="changes-header">
        <button type="button" class="icon-circle-btn" (click)="back.emit()" aria-label="Back">
          <span class="material-icons">chevron_left</span>
        </button>
        <div class="changes-header-text">
          <h2 class="changes-title">What changed</h2>
          <p class="changes-sub">{{ species.short }} · {{ sectorLabel }}</p>
        </div>
        <button type="button" class="icon-circle-btn" (click)="close.emit()" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="changes-content">
        @if (isLoading()) {
          <div class="loading-state">
            <div class="spinner"></div>
          </div>
        } @else if (error()) {
          <p class="sub-text">{{ error() }}</p>
        } @else if (events().length === 0) {
          <div class="skipper-says-row">
            <div class="skipper-avatar-circle">
              <img src="/assets/common_icons/vectormarine.svg" alt="Skipper" />
            </div>
            <div class="skipper-bubble">
              Nothing has changed for {{ species.short.toLowerCase() }} lately. I’ll tell you when it does.
            </div>
          </div>
        } @else {
          <div class="events-list">
            @for (e of events(); track e.id) {
              <div class="change-card">
                <div class="change-card-title">{{ e.title }}</div>
                <div class="change-card-text">{{ e.text }}</div>
                <div class="change-card-date">{{ formatDate(e.at) }}</div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .changes-page {
        display: flex;
        flex-direction: column;
        height: 100%;
        background: #ffffff;
      }
      .changes-header {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #ededf0;
      }
      .changes-header-text {
        flex: 1;
        margin: 0 10px;
      }
      .changes-title {
        font-size: 17px;
        font-weight: 600;
        color: #1c1c1e;
        margin: 0;
      }
      .changes-sub {
        font-size: 12.5px;
        color: #8a8a8e;
        margin: 2px 0 0;
      }
      .icon-circle-btn {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        border: none;
        background: #f2f2f6;
        color: #3a3a3c;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      .icon-circle-btn .material-icons {
        font-size: 20px;
      }
      .changes-content {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
      }
      .loading-state {
        display: flex;
        justify-content: center;
        padding: 32px 0;
      }
      .spinner {
        width: 28px;
        height: 28px;
        border: 3px solid #eaf2fe;
        border-top-color: #2463eb;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      .skipper-says-row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
      }
      .skipper-avatar-circle {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: #eaf2fe;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
      }
      .skipper-avatar-circle img {
        width: 20px;
        height: 20px;
      }
      .skipper-bubble {
        flex: 1;
        padding: 12px 15px;
        background: #eef5fe;
        border: 1px solid #d9e7fc;
        border-radius: 18px;
        font-size: 15px;
        color: #2c2c2e;
        line-height: 1.38;
      }
      .events-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .change-card {
        padding: 14px 16px;
        background: #ffffff;
        border: 1px solid #e5e5ea;
        border-radius: 16px;
      }
      .change-card-title {
        font-size: 15.5px;
        font-weight: 600;
        color: #1c1c1e;
      }
      .change-card-text {
        font-size: 14.5px;
        color: #1c1c1e;
        line-height: 1.4;
        margin: 4px 0 6px;
      }
      .change-card-date {
        font-size: 13px;
        color: #8a8a8e;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeepItChangesPageComponent implements OnInit {
  private api = inject(RegsApiService);

  @Input({ required: true }) species!: Species;
  @Input({ required: true }) sector!: Sector;

  @Output() back = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  readonly events = signal<ChangeEvent[]>([]);
  readonly isLoading = signal(true);
  readonly error = signal<string | null>(null);

  get sectorLabel(): string {
    return this.sector === 'recreational' ? 'Recreational' : 'Commercial';
  }

  ngOnInit(): void {
    this.loadChanges();
  }

  async loadChanges(): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const followId = Followable.idFor(this.species.id, this.sector);
      const evs = await this.api.changes([followId]);
      this.events.set(evs);
    } catch (e: any) {
      this.error.set(e?.message || 'Changes unavailable right now.');
    } finally {
      this.isLoading.set(false);
    }
  }

  formatDate(d: Date): string {
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
